import { shape, func, string, bool, object } from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { profileLoading } from '../selectors/PersonProfileSelectors';
import history from '../utils/history';
import UserIcon from '../../../img/sprite/user.svg';
import helpIcon from '../../../img/sprite/questionmark-icon.svg';
import { PATH_PROFILE, PATH_LOGGED_OUT, ENABLE_ORG_MANAGEMENT } from '../constants';

import NewFeaturesToolTip from './NewFeaturesToolTip';

const SharedStyled = css`
  display: flex;
  justify-content: space-between;
  align-content: center;
  font-size: 12px;
  position: relative;
`;

const HeaderWrapper = styled.div`
  ${({ isLoggedIn }) => isLoggedIn ? 'justify-content: space-between' : 'justify-content: flex-end'};
  display: flex;
  align-items: center;
  margin-right: 1.5em;
  width: 18.125em;

  @media(max-width: 720px){
    display: flex;
    width: 100%;
    margin: 8px 8px 0 0;
    max-width: 290px;
  }
`;
HeaderWrapper.displayName = 'HeaderWrapper';

const StyledLink = styled(Link)`${SharedStyled}`;
StyledLink.displayName = 'StyledLink';

const StyledAnchor = styled.a`
  ${SharedStyled}
`;
StyledAnchor.displayName = 'StyledAnchor';

const LinkContainer = styled.div`
  ${SharedStyled}
`;
LinkContainer.displayName = 'LinkContainer';

const Svg = styled.svg`
  height: 1.5em;
  width: 1.5em;
  margin-right: 0.333em;
`;
Svg.displayName ='Svg';

const SpanMargin = styled.span`
  margin-bottom: 5px;
`;
SpanMargin.displayName = 'SpanMargin';

const EmptyContainer = styled.div`
  /* fill space of LinkContainer when it is not rendered */
  width: 64px;
`;

const UserLogoAndText = ({ customer, profileLoadingState }) => {
  if (profileLoadingState === 'success') {
    return (
      <LinkContainer>
        <Svg>
          <use xlinkHref={`#${UserIcon.id}`} />
        </Svg>
        <span>
          Hi, {customer.firstName}
        </span>
      </LinkContainer>
    );
  } else {
    return <EmptyContainer></EmptyContainer>;
  }
};

UserLogoAndText.propTypes = {
  customer: shape({
    firstName: string
  })
};

const ProfileLink = ({ isRootPage, customer, profileLoadingState }) => (
  <React.Fragment>
    {isRootPage ?
        <UserLogoAndText customer={customer} profileLoadingState={profileLoadingState}  />
      :
      <StyledLink to="/" data-qa-hook="myLifeWay.link">
        <UserLogoAndText customer={customer} profileLoadingState={profileLoadingState}  />
      </StyledLink>
      }
  </React.Fragment>
);

ProfileLink.propTypes = {
  isRootPage: bool,
  customer: shape({
    firstName: string
  }),
  profileLoadingState: string
};

const HeaderLinks = ({ isLoggedIn, path, navToggle, isMobile, customer, onLogOut }) => {
  const isRootPage = path === PATH_PROFILE;
  const profileLoadingState = useSelector(profileLoading);

  const signOutClick = () => {
    if (isMobile) {
      navToggle();
    }
    onLogOut();
    history.push(PATH_LOGGED_OUT);
  };

  return (
    <HeaderWrapper isLoggedIn={isLoggedIn}>
      {isMobile ?
        isLoggedIn ?
            <React.Fragment>
              <ProfileLink isRootPage={isRootPage} customer={customer} profileLoadingState={profileLoadingState} />
              <StyledLink onClick={signOutClick}>
                Sign Out
              </StyledLink>
            </React.Fragment>
          :
            null
        :
          isLoggedIn ?
            <React.Fragment>
              {ENABLE_ORG_MANAGEMENT && <NewFeaturesToolTip />}
              <ProfileLink isRootPage={isRootPage} customer={customer} profileLoadingState={profileLoadingState} />
              <StyledAnchor
                href="https://www.lifeway.com#help"
                rel="noopener noreferrer"
                target="_blank"
                data-qa-hook="help.link"
              >
                <Svg>
                  <use xlinkHref={`#${helpIcon.id}`} />
                </Svg>
                <span>Help</span>
              </StyledAnchor>
              <StyledAnchor
                onClick={signOutClick}
                data-qa-hook="signOut.link"
              >
                <SpanMargin>Sign Out</SpanMargin>
              </StyledAnchor>
            </React.Fragment>
          :
            <React.Fragment>
              <StyledAnchor
                href="https://www.lifeway.com#help"
                rel="noopener noreferrer"
                target="_blank"
                data-qa-hook="help.link"
              >
                <Svg>
                  <use xlinkHref={`#${helpIcon.id}`} />
                </Svg>
                <span>Help</span>
              </StyledAnchor>
            </React.Fragment>
      }
    </HeaderWrapper>
  );
};

HeaderLinks.propTypes = {
  isLoggedIn: bool,
  path: string,
  isMobile: bool,
  navToggle: func,
  customer: object,
  onLogOut: func
};

export default HeaderLinks;
